import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styles from './AddNewComplaint.module.css';
import { createComplaint, lookupSearch } from '../ComplaintsApi';
import Error from '../Error';

// ── Animation Variants ────────────────────────────────────────────────────
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } },
  exit: { opacity: 0, transition: { duration: 0.2, delay: 0.05 } },
};

const modalVariants = {
  hidden: { opacity: 0, y: 48, scale: 0.96 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0, y: 40, scale: 0.96,
    transition: { duration: 0.22, ease: 'easeIn' },
  },
};

const fieldGroupVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04, delayChildren: 0.1 } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
};

// ── Which types span full row ─────────────────────────────────────────────
const FULL_WIDTH_TYPES = ['textarea'];

// ── Form Field Wrapper ────────────────────────────────────────────────────
function FormField({ label, required, error, touched, children, fullWidth }) {
  return (
    <motion.div
      className={`${styles.formGroup} ${fullWidth ? styles.full : ''}`}
      variants={fieldVariants}
    >
      <label>
        {label}
        {required && <span className={styles.requiredStar}> *</span>}
      </label>
      {children}
      <AnimatePresence>
        {touched && error && (
          <motion.span
            className={styles.fieldError}
            initial={{ opacity: 0, y: -4, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -4, height: 0 }}
            transition={{ duration: 0.18 }}
          >
            {error}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Reference / Lookup Field ──────────────────────────────────────────────
function ReferenceField({ field, onChange, onBlur }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const limit = field.length > 0 ? field.length : undefined;

  const handleSearch = useCallback(async (q) => {
    setQuery(q);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const referenceTo = (field.referenceTo.length >= 1 ? field.referenceTo.find(r => r === "User") : field.referenceTo[0])|| field.referenceTo[0];
      const res = await lookupSearch(referenceTo, q);
      setResults(res.results ?? []);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [field.referenceTo]);

  const handleSelect = (item) => {
    setSelected(item);
    setQuery(item.Name);       // show Name in input
    setOpen(false);
    onChange(field.name, item.Id);  // store Id in formik
  };

  const handleClear = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
    setOpen(false);
    onChange(field.name, '');
  };

  return (
    <div className={styles.referenceWrap}>
      <div className={styles.referenceInputRow}>
        <input
          type="text"
          placeholder={`Search ${field.label}…`}
          value={query}
          maxLength={limit}
          onChange={(e) => handleSearch(e.target.value)}
          onBlur={onBlur}
          autoComplete="off"
          readOnly={!!selected}
          className={selected ? styles.inputReadonly : ''}
        />
        {selected && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClear}
            title="Clear selection"
          >
            &times;
          </button>
        )}
        {loading && <span className={styles.lookupSpinner} />}
      </div>

      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.ul
            className={styles.lookupDropdown}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
          >
            {results.map((item) => (
              <li
                key={item.Id}
                className={styles.lookupItem}
                onMouseDown={() => handleSelect(item)}
              >
                {item.Name}
              </li>
            ))}
          </motion.ul>
        )}
        {open && results.length === 0 && !loading && (
          <motion.ul
            className={styles.lookupDropdown}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <li className={styles.lookupEmpty}>No results found</li>
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Dynamic Field Renderer ────────────────────────────────────────────────
function DynamicField({ field, formik }) {
  const fieldName = field.name;
  const fieldError = formik.errors[fieldName];
  const touched = formik.touched[fieldName];
  const maxLength = field.length > 0 ? field.length : undefined;
  const isFullWidth = FULL_WIDTH_TYPES.includes(field.type);

  if (field.type === 'textarea') {
    return (
      <FormField label={field.label} required={field.required} error={fieldError} touched={touched} fullWidth={isFullWidth}>
        <textarea
          rows={4}
          placeholder={`Enter ${field.label.toLowerCase()}…`}
          maxLength={maxLength}
          {...formik.getFieldProps(fieldName)}
          className={touched && fieldError ? styles.inputError : ''}
        />
      </FormField>
    );
  }

  if (field.type === 'date') {
    return (
      <FormField label={field.label} required={field.required} error={fieldError} touched={touched} fullWidth={isFullWidth}>
        <input
          type="date"
          {...formik.getFieldProps(fieldName)}
          className={touched && fieldError ? styles.inputError : ''}
        />
      </FormField>
    );
  }

  if (field.type === 'boolean') {
    return (
      <motion.div
        className={`${styles.formGroup} ${styles.checkboxGroup}`}
        variants={fieldVariants}
      >
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            checked={!!formik.values[fieldName]}
            onChange={(e) => formik.setFieldValue(fieldName, e.target.checked)}
            onBlur={() => formik.setFieldTouched(fieldName, true)}
          />
          <span className={styles.checkboxCustom} />
          <span className={styles.checkboxText}>{field.label}</span>
        </label>
      </motion.div>
    );
  }

  if (field.type === 'picklist') {
    const activeOptions = field.picklistValues.filter((o) => o.active);
    return (
      <FormField label={field.label} required={field.required} error={fieldError} touched={touched} fullWidth={isFullWidth}>
        <select
          {...formik.getFieldProps(fieldName)}
          className={touched && fieldError ? styles.inputError : ''}
        >
          <option value="">Select {field.label}</option>
          {activeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </FormField>
    );
  }

  if (field.type === 'reference') {
    return (
      <FormField label={field.label} required={field.required} error={fieldError} touched={touched} fullWidth={isFullWidth}>
        <ReferenceField
          field={field}
          value={formik.values[fieldName]}
          onChange={formik.setFieldValue}
          onBlur={() => formik.setFieldTouched(fieldName, true)}
        />
      </FormField>
    );
  }

  if (field.type === 'double') {
    return (
      <FormField label={field.label} required={field.required} error={fieldError} touched={touched} fullWidth={isFullWidth}>
        <input
          type="number"
          step="any"
          placeholder={`Enter ${field.label.toLowerCase()}`}
          {...formik.getFieldProps(fieldName)}
          className={touched && fieldError ? styles.inputError : ''}
        />
      </FormField>
    );
  }

  return (
    <FormField label={field.label} required={field.required} error={fieldError} touched={touched} fullWidth={isFullWidth}>
      <input
        type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
        placeholder={`Enter ${field.label.toLowerCase()}`}
        maxLength={maxLength}
        {...formik.getFieldProps(fieldName)}
        className={touched && fieldError ? styles.inputError : ''}
      />
    </FormField>
  );
}

// ── Build Yup schema dynamically ──────────────────────────────────────────
function buildValidationSchema(fields) {
  const shape = {};
  fields.forEach((f) => {
    if (f.required) {
      shape[f.name] = f.type === 'boolean'
        ? Yup.bool().oneOf([true], `${f.label} is required`)
        : Yup.string().required(`${f.label} is required`);
    }
  });
  return Yup.object(shape);
}

// ── Build initial values dynamically ─────────────────────────────────────
function buildInitialValues(fields) {
  const values = {};
  const today = new Date().toISOString().split('T')[0];
  fields.forEach((f) => {
    if (f.type === 'boolean') values[f.name] = false;
    else if (f.type === 'date') values[f.name] = today;
    else values[f.name] = '';
  });
  return values;
}

// ── Build payload — skip empty non-boolean fields ─────────────────────────
function buildPayload(values, fields) {
  const payload = {};
  fields.forEach((f) => {
    const v = values[f.name];
    if (f.type === 'boolean') {
      payload[f.name] = v;
    } else if (v !== '' && v !== null && v !== undefined) {
      payload[f.name] = v;
    }
  });
  return payload;
}

// ── Main Component ────────────────────────────────────────────────────────
export default function AddNewComplaint({ isOpen, onClose, onCreated, createFields = [], isLoading }) {
  const [submitError, setSubmitError] = useState(null);

  const queryClient = useQueryClient();

  const { mutate: submitComplaint, isPending: isSubmitting } = useMutation({
    mutationFn: createComplaint,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tableComplaints'] });
      onCreated?.();
      formik.resetForm();
      setSubmitError(null);
      onClose?.();
    },
    onError: (err) => {
      setSubmitError(err.message || "Failed to create complaint. Please try again.");
    },
  });

  const validationSchema = React.useMemo(
    () => buildValidationSchema(createFields), [createFields]
  );

  const initialValues = React.useMemo(
    () => buildInitialValues(createFields), [createFields]
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const payload = buildPayload(values, createFields);
      submitComplaint(payload);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setSubmitError(null);
    onClose?.();
  };
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // Disable Create if any required field is empty
  const isFormInvalid = createFields.some((f) => {
    if (!f.required) return false;
    const v = formik.values[f.name];
    if (f.type === 'boolean') return v !== true;
    return !v || v === '';
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.overlay}
          onClick={handleOverlayClick}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={styles.modalHeader}>
              <h2>New Complaint</h2>
              <motion.button
                className={styles.closeBtn}
                onClick={handleClose}
                whileHover={{ scale: 1.12 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                &times;
              </motion.button>
            </div>

            {/* Form */}
            <form onSubmit={formik.handleSubmit}>
              <div className={styles.modalBody}>
                {isLoading ? (
                  <p>Loading fields…</p>
                ) : (
                  <motion.div
                    className={styles.formGrid}
                    variants={fieldGroupVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {createFields.map((field) => (
                      <DynamicField key={field.name} field={field} formik={formik} />
                    ))}
                  </motion.div>
                )}
              </div>

              {/* Footer */}
              <motion.div
                className={styles.modalFooter}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.2 }}
              >
                {submitError && (<Error errorMsg={submitError} />)}

                <motion.button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={handleClose}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  disabled={isSubmitting || isLoading || isFormInvalid}
                  whileHover={{ scale: (isSubmitting || isFormInvalid) ? 1 : 1.03 }}
                  whileTap={{ scale: (isSubmitting || isFormInvalid) ? 1 : 0.97 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {isSubmitting ? (
                    <motion.span
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                    >
                      Creating…
                    </motion.span>
                  ) : (
                    'Create Complaint'
                  )}
                </motion.button>
              </motion.div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
